import request from '@/scripts/request';
import { myClientStorage } from '@/scripts/utils';
import dbWorkerManager from '@/scripts/dataBase';
import { ActionType } from '@/scripts/dataBase/type';
import { getAvatar } from '@/scripts/utils/genAvatar';
import type { UserResponse } from '@/pages/login/interface';
import type { MyAxiosResponseData } from '@/scripts/request/types';
import type {
  UserSettings,
  UserSettingsKeys,
  UserSettingsParams,
} from '@/contexts/session/hooks/useUserSettings/types';
import { exportApis } from '@/scripts/utils/devUtils';
import { userStorage, userStorage_client } from '../storages';

export interface User {
  access?: MyRecord<string[]> | '*';
  avatar?: string;
  createdAt?: number;
  updatedAt?: number;
  disabled?: boolean;
  email?: string;
  token?: string;
  name: string;
  username?: string;
  userId: string;
  expire_duration?: number;
}

export const userApis = {
  postFCMToken(params: { device_id: string; device_type: number; push_token: string }) {
    return request('/api/v1/user/info/upload-token', {
      method: 'POST',
      data: params,
      disableErrorEvent: true,
    }).then(({ data }) => {
      return data;
    });
  },
  login(params: MyRecord) {
    return request('/api/v1/user/login', {
      method: 'POST',
      data: params,
      disableErrorEvent: true,
    }).then(async ({ data }: { data: UserResponse }) => {
      data.avatar = getAvatar(data.avatar, data.name || data.email);
      return data;
    });
  },
  getUserSettings(keys?: UserSettingsKeys[]) {
    return request<MyAxiosResponseData<UserSettings>>(`/api/v1/user/props`, {
      params: { keys },
    }).then((res) => {
      if (res.data?.expire_duration) {
        res.data.expire_duration = Number(res.data.expire_duration);
      }
      return res.data;
    });
  },
  setUserSettings(params: UserSettingsParams) {
    const { expire_duration, ...rest } = params;
    return request(`/api/v1/user/props`, {
      method: 'POST',
      data: {
        ...rest,
        expire_duration: expire_duration ? expire_duration.toString() : undefined,
      },
    });
  },
  logout() {
    return request('/api/v1/user/logout', {
      method: 'POST',
      disableErrorEvent: true,
      msg: true,
    }).then(async ({ data }) => {
      await this.removeUserStorage();
      return data;
    });
  },
  setToken(token: string) {
    return userStorage.setItem('token', token);
  },
  getToken() {
    return userStorage.getItem('token');
  },
  isLogin() {
    return Boolean(this.getToken() && this.getUserId());
  },
  setUserId(userId: string) {
    userStorage.setItem('id', userId);
  },
  getUserId() {
    return userStorage.getItem('id');
  },
  async setUser(user: UserResponse) {
    const userInfo: User = {
      access: '*',
      disabled: false,
      createdAt: user.createdAt,
      updatedAt: user.updatedAt,
      avatar: user.avatar,
      email: user.email,
      token: user.token,
      name: user.name,
      userId: user.userId,
      username: user.username || '',
    };
    await userStorage_client.setItem('user', userInfo);
  },
  async getUser() {
    let user = await userStorage_client.getItem('user');
    if (!user) {
      user = userStorage.getItem('clear_DB_user');
      userStorage_client.setItem('user', user);

      userStorage.removeItem('clear_DB_user');
    }
    return user as User;
  },
  async removeUserStorage() {
    userStorage.removeItem('token');
    userStorage.removeItem('id');
    await userStorage_client.removeItem('user');
  },
  search(keyword = '') {
    const abortController = new AbortController();

    const p = keyword
      ? request('/api/v1/user/search', {
          signal: abortController.signal,
          params: {
            keyword,
          },
        }).then((res) =>
          res.data.list.map((item: MyRecord) =>
            Object.assign(item, {
              label: item.name,
              value: item.userId,
            })
          )
        )
      : Promise.resolve([]);

    return Object.assign(p, { abortController });
  },
};

export let isInitial = false;

export async function setInitialed() {
  if (!isInitial) {
    await myClientStorage.setItem('isInitial', true);
    isInitial = true;
  }
}

export async function setInitial() {
  isInitial = (await myClientStorage.getItem('isInitial')) || false;
}

export const userDB = {
  async init(userId: string) {
    await dbWorkerManager.sendWorkerMessage(ActionType.init, {
      dbName: userId,
    });
    await setInitial();
  },
  close() {
    return dbWorkerManager.sendWorkerMessage(ActionType.close);
  },
};

exportApis({ userApis });
